/**
 * TeamManagementModal — dedicated modal wrapping TeamManagementPanel.
 *
 * Used by the dashboard (Phase D.4) when the user clicks "Adjust Team
 * Members" from the scope selector's footer. Provides a focused, full-
 * height surface for adding and removing teammates without competing
 * with the dashboard's main layout.
 *
 * TransferFundsModal does NOT use this — it renders TeamManagementPanel
 * inline inside its own modal (an "expand a section" UX that fits
 * better in that context).
 *
 * Each add/remove commits immediately via the panel's internal handlers,
 * so the footer only needs a "Done" close action.
 */

import { X, Users } from 'lucide-react';
import { TeamManagementPanel } from './TeamManagementPanel';
import { type Teammate } from './TeammateSelector';

interface TeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  /** Current teammates list (controlled by parent). */
  teammates: Teammate[];
  /**
   * Fires whenever the teammates list changes (after a successful add
   * or remove). Parent should replace its own list with the value here.
   */
  onTeammatesChange: (teammates: Teammate[]) => void;
}

export function TeamManagementModal({
  isOpen,
  onClose,
  locationId,
  teammates,
  onTeammatesChange,
}: TeamManagementModalProps) {
  if (!isOpen) return null;

  const handleAdded = (t: Teammate) => onTeammatesChange([...teammates, t]);
  const handleRemoved = (t: Teammate) =>
    onTeammatesChange(teammates.filter(x => x.id !== t.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Manage Team Members</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          <TeamManagementPanel
            locationId={locationId}
            teammates={teammates}
            onTeammateAdded={handleAdded}
            onTeammateRemoved={handleRemoved}
          />
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
